import { Inject, Injectable, Optional } from '@angular/core';
import { getReferral } from '@office-start/telemetry-utility';

import { ApplicationError } from '../../error-handler/error-handler.service';
import { ServiceWorkerState, ServiceWorkerStateService } from '../../service-worker/service-worker-state.service';
import { FileType } from '../models/file-item.model';
import { DataSecurityService } from '../services/data-security.service';
import { DeviceDetector, NetworkType } from '../services/device-detector.service';
import { OfficeRouteService } from '../services/office-route.service';
import { Performance } from '../services/performance.service';
import { INSTRUMENTATION_QUEUE } from './instrumentation-queue.injection-token';
import { InstrumentationEvent, InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents, KnownReferralPlatform, PiiString } from './instrumentation.service';

/**
 * Interface for the factory service responsible for creating instances of instrumentation events.
 */
export interface DispatcherEventFactoryService {
    /**
     * Creates instance of InstrumentationEvent object with given set of param/value pairs.
     *
     * @param streamName Instrumentation event stream name
     * @param params The object which maps instrumentation parameters with values.
     * @param metrics Array of metrics that have to be incremented as a result of recording event.
     * @returns InstrumentationEvent object which can be used to record the instrumentation
     * event with set of parameters specified in instrumentationData.
     */
    createEvent(
        streamName: string,
        params: InstrumentationParams,
        metrics: Array<string>): InstrumentationEvent;

    /**
    * Flushes any buffered events managed by the factory and closes the sink.
    */
    flush();
}

/**
 * Internal class which keeps track of generated instrumentation event.
 */
class DispatcherInstrumentationEvent implements InstrumentationEvent {
    constructor(
        private readonly instrumentationService: DispatcherInstrumentationService,
        public streamName: string,
        public params: InstrumentationParams,
        public metrics: Array<string>,
        public securityThreshold: DataSecurityLevel
    ) {
    }

    public record(): void {
        this.instrumentationService.recordEvent(this);
    }
}

interface OfficePageNav {
    time: number;
    from: string;
    to: string;
}

/**
 * Class which enables logging using multiple instrumentation libraries at the same time.
 */
@Injectable()
export class DispatcherInstrumentationService implements InstrumentationService {
    private registeredEventFactories: Array<DispatcherEventFactoryService> = [];
    private routeSubscriptionInitialized: boolean;
    private queuedEventsPopulatedWithRoute: boolean;
    private initialPage = 'Unknown';
    private serviceWorkerStateService: ServiceWorkerStateService = null;
    private lastPageNav: OfficePageNav = {
        time: 0,
        from: undefined,
        to: undefined
    };
    private static readonly lastPageNavTimeout = 1000;

    constructor(
        @Optional() @Inject(INSTRUMENTATION_QUEUE) private readonly queuedEvents: Array<QueuedAppEvent>,
        private readonly dataSecurityService: DataSecurityService,
        private readonly deviceDetector: DeviceDetector
        ) {
    }

    public registerServiceWorkerStateService(serviceWorkerStateService: ServiceWorkerStateService, setServiceWorkerStateForQueuedEvents: boolean) {
        if (this.serviceWorkerStateService) {
            throw new ApplicationError('DispatcherInstrumentationService.registerServiceWorkerStateService is not allowed to be called more than once');
        }

        if (!serviceWorkerStateService) {
            throw new ApplicationError('Must specify valid ServiceWorkerStateService when calling DispatcherInstrumentationService.registerServiceWorkerStateService');
        }

        this.serviceWorkerStateService = serviceWorkerStateService;

        if (setServiceWorkerStateForQueuedEvents) {
            // Populate service worker state property of queued events that were logged before ServiceWorkerStateService was registered with this instance
            this.queuedEvents.forEach(queuedEvent => queuedEvent.eventParams[InstrumentationParam.ServiceWorkerState] = this.serviceWorkerStateService.currentState);
        }
    }

    public registerOfficeRouteService(officeRouteService: OfficeRouteService) {
        if (this.routeSubscriptionInitialized) {
            throw new ApplicationError('DispatcherInstrumentationService.registerOfficeRouteService is not allowed to be called more than once');
        }

        if (!officeRouteService) {
            throw new ApplicationError('Must specify valid OfficeRouteService when calling DispatcherInstrumentationService.registerOfficeRouteService');
        }

        this.routeSubscriptionInitialized = true;

        officeRouteService.subscribeToRouteChangeStart(event => {
            const validRoute = !!event.currentRoute && !!event.currentRoute.route;
            const newPage = validRoute ? `${event.currentRoute.app ? FileType[event.currentRoute.app] : ''}${event.currentRoute.route}` : 'Unknown';
            this.lastPageNav = {
                time: Performance.now(),
                from: this.lastPageNav.to,
                to: newPage
            };
            if (this.initialPage === 'Unknown' && newPage !== 'Unknown') {
                this.initialPage = newPage;
            }
            if (!this.queuedEventsPopulatedWithRoute && validRoute) {
                // Populate page property of queued events
                this.queuedEvents.forEach(queuedEvent => queuedEvent.eventParams[InstrumentationParam.Page] = newPage);
                this.queuedEvents.forEach(queuedEvent => queuedEvent.eventParams[InstrumentationParam.InitialPage] = this.initialPage);
                this.queuedEventsPopulatedWithRoute = true;
            }
        });
    }

    public registerEventFactories(factories: Array<DispatcherEventFactoryService>) {

        if (this.registeredEventFactories.length > 0) {
            throw new ApplicationError('DispatcherInstrumentationService.registerEventFactories is not allowed to be called more than once');
        }

        if (!factories || factories.length === 0) {
            throw new ApplicationError('Must specify at least one event factory when calling DispatcherInstrumentationService.registerEventFactories');
        }

        // Make a copy of the passed array
        this.registeredEventFactories = factories.slice();

        // Drain queued events for all apps
        this.drainQueuedEvents();
    }

    public logGenericEvent(
        eventName: string,
        params: InstrumentationParams = {},
        metrics: Array<string> = [],
        securityThreshold: DataSecurityLevel = DataSecurityLevel.Security
    ) {
        this.generateEvent(eventName, KnownInstrumentationEvents.GenericEvent, securityThreshold, params, metrics, true);
    }

    public logImpressionEvent(
        eventName: string,
        params: InstrumentationParams = {},
        metrics: Array<string> = [],
        securityThreshold: DataSecurityLevel = DataSecurityLevel.Security
    ) {
        this.generateEvent(eventName, KnownInstrumentationEvents.GenericImpression, securityThreshold, params, metrics, true);
    }

    public logRequestEvent(
        eventName: string,
        httpCode: number,
        latency: number,
        error?: string | PiiString,
        fromCache = false,
        params: InstrumentationParams = {},
        metrics: Array<string> = [],
        securityThreshold: DataSecurityLevel = DataSecurityLevel.Security
    ) {
        this.generateRequestEvent(eventName, httpCode, latency, error, fromCache, params, metrics, securityThreshold).record();
    }

    public logClickEvent(
        eventName: string,
        elementId: string,
        area: string,
        referral?: string,
        referralPlatform?: string,
        params: InstrumentationParams = {},
        metrics: Array<string> = [],
        securityThreshold: DataSecurityLevel = DataSecurityLevel.Security,
        isPageNavEvent: boolean = false
    ) {
        metrics = metrics || [];

        // Log metric that uniquely identifies click event only for known areas/elements
        if (area && elementId && elementId !== 'Unknown') {
            metrics.push(`${eventName}.${area}.${elementId}`);
        }

        params[InstrumentationParam.ElementId] = elementId;

        if (referral) {
            const safeReferral = getReferral(referral);
            params[InstrumentationParam.Referral] = safeReferral;
            metrics.push(`Referral.${safeReferral}`);

            params[InstrumentationParam.ReferralPlatform] = referralPlatform && this.dataSecurityService.securityLevel >= DataSecurityLevel.Enhanced ? referralPlatform : KnownReferralPlatform.Web;
        }

        if (area) {
            params[InstrumentationParam.Area] = area;
        }

        // Click events involved in page transitions are often given the wrong page value automatically. We're manually overriding them here if we detect a recent navigation.
        // TODO: This is really a bandage solution on a larger systematic issue with how we log Page. There are other events, including impressions, requests, etc. that also
        // end up receiving an incorrect value for reasons that aren't completely understood. These page nav clicks are singled out mainly because by their nature they have a
        // very high error rate when it comes to assigning page. Ideally we would have a generic solution that fixes this for all events, but the implementation of such a fix
        // is still unclear.
        if (isPageNavEvent && this.lastPageNav && this.lastPageNav.from && (Performance.now() - this.lastPageNav.time < DispatcherInstrumentationService.lastPageNavTimeout)) {
            params[InstrumentationParam.Page] = this.lastPageNav.from;
        }

        this.generateEvent(eventName, KnownInstrumentationEvents.GenericClick, securityThreshold, params, metrics, true);
    }

    public logErrorEvent(
        eventName: string,
        params: InstrumentationParams = {},
        metrics: Array<string> = [],
        securityThreshold: DataSecurityLevel = DataSecurityLevel.Security
    ) {
        this.generateEvent(eventName, KnownInstrumentationEvents.AppError, securityThreshold, params, metrics, true);
    }

    public logPerformanceEvent(
        eventName: string,
        latency: number,
        params: InstrumentationParams = {},
        securityThreshold: DataSecurityLevel = DataSecurityLevel.Security
    ) {
        params[InstrumentationParam.Latency] = latency.toString();
        return this.generateEvent(eventName, KnownInstrumentationEvents.PerformanceDump, securityThreshold, params, [], true);
    }

    public logDebugCacheEvent(
        eventName: string,
        params: InstrumentationParams = {},
        metrics: Array<string> = [],
        securityThreshold: DataSecurityLevel = DataSecurityLevel.Security
    ) {
        return this.generateEvent(eventName, KnownInstrumentationEvents.GenericDebugCache, securityThreshold, params, metrics, true);
    }

    public applyGlobalStateParams(
        params: InstrumentationParams,
        overrideIfExist: boolean
    ) {
        // Applicable only to PWA where app is prelaunched but user hasn't interacted with it
        if (window.prelaunchMode) {
            params[InstrumentationParam.PrelaunchMode] = 'true';
        }

        if (overrideIfExist || !params.hasOwnProperty(InstrumentationParam.Page)) {
            params[InstrumentationParam.Page] = this.lastPageNav.to || 'Unknown';
        }

        if (overrideIfExist || !params.hasOwnProperty(InstrumentationParam.InitialPage)) {
            params[InstrumentationParam.InitialPage] = this.initialPage;
        }

        if (overrideIfExist || !params.hasOwnProperty(InstrumentationParam.ServiceWorkerState)) {
            params[InstrumentationParam.ServiceWorkerState] = this.serviceWorkerStateService ? this.serviceWorkerStateService.currentState : ServiceWorkerState.Unknown;
        }

        if (overrideIfExist || !params.hasOwnProperty(InstrumentationParam.AppSecurityLevel)) {
            params[InstrumentationParam.AppSecurityLevel] = this.dataSecurityService.securityLevel.toString();
        }

        if (overrideIfExist || !params.hasOwnProperty(InstrumentationParam.NetworkType)) {
            params[InstrumentationParam.NetworkType] = NetworkType[this.deviceDetector.getNetworkInfo()];
        }

        if (window.hostSessionId) {
            params[InstrumentationParam.HostSessionId] = window.hostSessionId;
        }

        if (window.ringId) {
            params[InstrumentationParam.Ring] = window.ringId;
        }
    }

    public flush() {
        // Drain queued events in case any were added after service creation
        this.drainQueuedEvents();
        this.registeredEventFactories.forEach(factory => factory.flush());
    }

    public recordEvent(event: DispatcherInstrumentationEvent) {
        const isErrorEvent =
            event && event.params &&
            ((event.params[InstrumentationParam.EventName] && (event.params[InstrumentationParam.EventName] as string).indexOf('Error') > -1) ||
            (event.params[InstrumentationParam.Error] && event.params[InstrumentationParam.Error] !== 'OK'));
        if (this.dataSecurityService.securityLevel < event.securityThreshold || (window.disableLogs && !isErrorEvent)) {
            return;
        }

        if (this.registeredEventFactories.length > 0) {
            this.registeredEventFactories.forEach(factory => factory.createEvent(event.streamName, event.params, event.metrics).record());
        } else {
            const queuedEvent: QueuedAppEvent = {
                streamName: event.streamName,
                eventName: event.params['EventName'] as string,
                eventParams: event.params,
                metrics: event.metrics,
                securityThreshold: event.securityThreshold
            };
            this.queuedEvents.push(queuedEvent);
        }
    }

    public generateEvent(
        eventName: string,
        streamName: string,
        securityThreshold: DataSecurityLevel,
        params: InstrumentationParams,
        metrics: Array<string> = [],
        record: boolean = false
    ): InstrumentationEvent {
        params[InstrumentationParam.EventName] = eventName;

        this.applyGlobalStateParams(params, false /*do not override for events that explicitly set global state params*/);

        const error = params && params[InstrumentationParam.Error];
        if (error && typeof error === 'string') {
            // Some Global errors contain email addresses since they log the full page url, which can include a 'username' query param.
            // Redacting all emails here.
            params[InstrumentationParam.Error] = InstrumentationService.filterPii(error);
        }

        const debugInfo = params && params[InstrumentationParam.DebugInfo];
        if (debugInfo && typeof debugInfo === 'string') {
            params[InstrumentationParam.DebugInfo] = InstrumentationService.filterPii(debugInfo);
        }

        // Done here instead of generatePerformanceEvent because queued events don't go through that method
        if (streamName === KnownInstrumentationEvents.PerformanceDump && typeof window.startedFromServiceWorker !== 'undefined') {
            params[InstrumentationParam.CachedPage] = window.startedFromServiceWorker.toString();
        }

        // For ANTP, click logging can be disabled for the top half of the page (MSN components).  We still log clicks
        // for the Office components, but want to be able to pivot on the data, so we add a param here
        if (streamName === KnownInstrumentationEvents.GenericClick) {
            params[InstrumentationParam.ReportingEnabled] = window.isMetricsReportingEnabled ? window.isMetricsReportingEnabled.toString() : 'false';
        }

        const event = new DispatcherInstrumentationEvent(this, streamName, params, metrics, securityThreshold);
        if (record) {
            event.record();
        }
        return event;
    }

    public generateRequestEvent(
        eventName: string,
        httpCode: number,
        latency: number,
        error?: string | PiiString,
        fromCache = false,
        params: InstrumentationParams = {},
        metrics: Array<string> = [],
        securityThreshold: DataSecurityLevel = DataSecurityLevel.Security
    ): InstrumentationEvent {
        params[InstrumentationParam.HttpCode] = httpCode.toString();
        params[InstrumentationParam.Latency] = latency.toString();
        params[InstrumentationParam.FromServiceWorkerCache] = fromCache.toString();

        if (error) {
            params[InstrumentationParam.Error] = error;
        }

        return this.generateEvent(eventName, KnownInstrumentationEvents.GenericRequest, securityThreshold, params, metrics);
    }

    private drainQueuedEvents() {
        if (this.queuedEvents) {
            this.queuedEvents.splice(0, this.queuedEvents.length).forEach(event => {
                this.generateEvent(event.eventName, event.streamName, event.securityThreshold, event.eventParams || {}, event.metrics, true);
            });
        }
    }
}
